import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import IssuesTable from "./IssuesTable/IssuesTable";
import IssuesHeader from "./IssuesHeader/IssuesHeader";
import { dataQueryStatus } from "utils";
import API from "services/lib/api";
import { apiRoute } from "services";
import { Empty, Loading } from "components/ui";
import ErrorView from "components/ui/ErrorView/ErrorView";
import { getErrorMessage, getPageFromCache } from "utils/helper";
import CustomPagination from "components/dashboard/common/CustomPagination/CustomPagination";
import CreateIssueModal from "./IssuesModal/CreateIssueModal/CreateIssueModal";
import Filter from "components/dashboard/common/Filter/Filter";
import {
    setIssueBaseFilter,
    setIssueFilterParams,
    setIssueFilterOption,
} from "store/filters/actions";
import {
    saveIssues,
    clearIssues,
    clearConversations,
    clearDiscoveries,
} from "store/modules/sam/actions";
import "./Issues.scss";

const { IDLE, LOADING, DATAMODE, NULLMODE, ERROR } = dataQueryStatus;

const Issues = () => {
    const [showModal, toggleModal] = useState(false);
    const closeModal = () => toggleModal(false);
    const [tableIsLoading, setTableIsLoading] = useState(false);
    const [clearSearch, setClearSearch] = useState(false);
    const [useTableLoader, setUseTableLoader] = useState(false);
    const [searchIsLoading, setSearchIsLoading] = useState(false);
    const [errorMssg, setErrorMssg] = useState();
    const { baseFilter, filterOption, filterParams } = useSelector(
        (state) => state?.filters?.issueFilters
    );
    const [issues, setIssues] = useState([]);
    const {
        issuesCache: { pages, totalRecords },
    } = useSelector((state) => state?.sam);
    const [status, setStatus] = useState(
        pages[filterParams?.currentPage]?.length > 0 ? DATAMODE : IDLE
    );

    const dispatch = useDispatch();

    const getIssues = async (useTableLoader, httpRequest) => {
        try {
            setErrorMssg();

            useTableLoader && setTableIsLoading(true);
            if (issues?.length < 1 && filterParams?.search < 1) {
                setStatus(LOADING);
            }

            const url = apiRoute?.issues;
            const res = await API.get(url, {
                params: { ...filterParams },
                signal: httpRequest?.signal,
            });
            if (res.status === 200) {
                filterParams?.search?.length > 0 && setUseTableLoader(true);
                const { data, metaData } = res.data;
                let newStatus = data?.length > 0 ? DATAMODE : NULLMODE;
                setIssues(data);
                dispatch(
                    saveIssues(
                        filterParams?.currentPage,
                        metaData?.totalRecords,
                        data
                    )
                );
                setStatus(newStatus);
                setSearchIsLoading(false);
                setTableIsLoading(false);
            }
        } catch (err) {
            setSearchIsLoading(false);
            setErrorMssg(getErrorMessage(err));
            useTableLoader ? setTableIsLoading(false) : setStatus(ERROR);
        }
    };

    useEffect(() => {
        let httpRequest = new AbortController();

        const cachedPage = getPageFromCache(pages, filterParams.currentPage);
        if (cachedPage?.length > 0 && !searchIsLoading) {
            setIssues(cachedPage);
        } else {
            getIssues(useTableLoader, httpRequest);
        }

        return () => {
            if (!useTableLoader) {
                httpRequest.abort();
            }
        };
        // eslint-disable-next-line
    }, [filterParams, useTableLoader]);

    const handleSearch = (search) => {
        setUseTableLoader(true);
        setSearchIsLoading(true);
        setClearSearch(false);
        dispatch(setIssueFilterParams({ currentPage: 1, search }));
        dispatch(clearIssues());
    };

    const handlePageChange = (currentPage) => {
        setUseTableLoader(true);
        dispatch(setIssueFilterParams({ ...filterParams, currentPage }));
    };

    const handleBasefilter = (baseFilter) => {
        dispatch(setIssueBaseFilter(baseFilter));

        dispatch(setIssueFilterOption({}));
    };

    const handleSuccess = () => {
        setClearSearch(true);

        dispatch(clearIssues());
        dispatch(clearConversations());
        dispatch(clearDiscoveries());
        // dispatch(
        //     setIssueFilterParams({
        //         search: "",
        //         currentPage: 1,
        //         pageSize: 5,
        //     })
        // );
        getIssues();
    };

    const handleSortByAlpha = (value, option) => {
        setUseTableLoader(true);
        dispatch(
            setIssueFilterParams({
                ...filterParams,
                alphabetical: value,
                dateCreated: null,
                lastModified: null,
            })
        );
        dispatch(setIssueFilterOption({ ...option, value }));
        dispatch(clearIssues());
    };

    const handleSortByDate = (value, option) => {
        setUseTableLoader(true);
        dispatch(
            setIssueFilterParams({
                ...filterParams,
                dateCreated: value,
                alphabetical: null,
                lastModified: null,
            })
        );
        dispatch(setIssueFilterOption({ ...option, value }));
        dispatch(clearIssues());
    };

    const handleSortByLastModified = (value, option) => {
        dispatch(
            setIssueFilterParams({
                ...filterParams,
                dateCreated: null,
                alphabetical: null,
                lastModified: value,
            })
        );
        dispatch(setIssueFilterOption({ ...option, value }));
        dispatch(clearIssues());
    };

    const renderBasedOnStatus = () => {
        switch (status) {
            case IDLE:
                return "";

            case LOADING:
                return <Loading />;

            case ERROR:
                return (
                    <ErrorView message={errorMssg} handleRetry={getIssues} />
                );

            case NULLMODE:
                return (
                    <Empty
                        text={
                            useTableLoader
                                ? "No Issue matches the search."
                                : "No Issue has been created yet."
                        }
                    />
                );

            case DATAMODE:
                return (
                    <>
                        <IssuesTable
                            issues={issues}
                            tableIsLoading={tableIsLoading}
                            errorMssg={errorMssg}
                            handleSuccess={handleSuccess}
                        />
                        <CustomPagination
                            totalRecords={totalRecords}
                            handlePageChange={handlePageChange}
                            {...filterParams}
                        />
                    </>
                );

            default:
                return "";
        }
    };

    return (
        <div className='padded__layout'>
            <div className='issues'>
                <IssuesHeader toggleModal={toggleModal} />
                <Filter
                    handleSortByAlpha={handleSortByAlpha}
                    handleSortByDate={handleSortByDate}
                    hideClassFilter={true}
                    handleSearch={handleSearch}
                    searchIsLoading={searchIsLoading}
                    clearSearch={clearSearch}
                    searchPlaceholder={"Search issues here"}
                    handleSortByLastModified={handleSortByLastModified}
                    baseFilter={baseFilter}
                    filterOption={filterOption}
                    handleBasefilter={handleBasefilter}
                    searchDefaultValue={filterParams?.search}
                />
                {renderBasedOnStatus()}
            </div>
            {showModal && (
                <CreateIssueModal
                    closeModal={closeModal}
                    handleSuccess={handleSuccess}
                    showModal={showModal}
                />
            )}
        </div>
    );
};

export default Issues;
